<script setup lang="ts">
import type { FlightHydrated } from "@ca/db/flights";
import type { FlightProcessingBucket } from "@ca/types/flights";

import { computed } from "vue";

import { formatDayMonth, formatTimeToMinute } from "@ca/formatters/dates";
import {
	RotorWingRiskOutputSchema, 
} from "@ca/types/rotor_wing";

import { useFlightDuration } from "@/composables/flights";
import { isFlightProcessing } from "@/utils/flightProgress";

import FlightProcessingBadge from "./FlightProcessingBadge.vue";
import RiskBadge from "./RiskBadge.vue";
import RiskZoneList from "./RiskZoneList.vue";
const props = defineProps<{
	bucket?: FlightProcessingBucket;
	flight: FlightHydrated;
}>();

const processing = computed(() =>
	!props.flight.risk?.riskLevel && isFlightProcessing(props.bucket) ? props.bucket : null);

const times = computed(() => {
	const departureTs = props.flight.departureTime ?? props.flight.filedDepartureTime;
	const departureTZ = props.flight.originTz ?? props.flight.originAirport?.tz ?? "UTC";
	const arrivalTs = props.flight.arrivalTime ?? props.flight.filedArrivalTime;
	const arrivalTZ = props.flight.destinationTz ?? props.flight.destinationAirport?.tz ?? "UTC";
	const timeIndex = props.flight.timeIndex;
	return {
		departure: formatTimeToMinute(departureTs, departureTZ),
		arrival: formatTimeToMinute(arrivalTs, arrivalTZ),
		timeIndex: formatDayMonth(timeIndex, departureTZ),
	};
});

const duration = useFlightDuration(props.flight);
const rotorWingRisk = computed(() => {
	const result = RotorWingRiskOutputSchema.safeParse(props.flight.rotorWingRisk?.riskData);
	if (!result.success) {
		return null;
	}
	return result.data;
});

const riskSummary = computed(() => {
	return rotorWingRisk.value?.summary;
});
const zones = computed(() => {
	return rotorWingRisk.value?.zones ?? [];
});
</script>
  
<template>
  <UCard variant="subtle">
    <template #header>
      <div class="flex flex-col gap-2">
        <div class="flex gap-2 justify-between items-center">
          <FlightProcessingBadge
            v-if="processing"
            :bucket="processing"
          />
          <RiskBadge
            v-else
            :risk="flight.risk?.riskLevel"
          />
          <div class="font-cta text-right">
            <div>
              {{ times.timeIndex }} - {{ times.departure }} to {{ times.arrival }}
            </div>
            <div class="text-gray-500 text-xs leading-2">
              {{ duration }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-if="riskSummary">
      <h3 class="text-gray-500 text-sm font-cta mb-2">
        {{ flight.rotorWingRisk?.actualRisk != null ? "Actual Risk" : "Risk Breakdown" }}
      </h3>
      <p class="text-gray-600 text-sm leading-relaxed">
        {{ riskSummary }}
      </p>
      <div v-if="zones.length > 0">
        <h3 class="text-gray-500 text-sm font-cta mt-4 mb-2">
          Risk Zones
        </h3>
        <RiskZoneList
          mini
          :zones="zones"
        />
      </div>
    </div>
    <template #footer>
      <div class="flex items-center justify-end">
        <UButton
          label="View Flight"
          size="sm"
          :to="`/flights/${flight.id}`"
        />
      </div>
    </template>
  </UCard>
</template>
